import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { isBefore, isSameDay } from 'date-fns';
import { useAppContext } from 'src/app/state';
import { IoMdClose } from 'react-icons/io';
import Button from 'src/app/components/kit/button/Button';
import Text from 'src/app/components/kit/text/Text';
import DateTimePicker from 'src/app/components/kit/date-time-picker/DateTimePicker';
import { ActionType } from 'src/app/state/action-type.enum';
import { MigraineAttack } from 'src/types/migraine-attack.types';
import styles from './VyduraIntakePage.module.css';
import { getDateRange, getNextDate } from 'src/app/utils/calendar.utils';
import { AppRoutes } from 'src/types/app-routes.enum';
import { isDateInList } from 'src/app/utils/calendar.utils';

const VyduraIntakePage = () => {
  const navigate = useNavigate();
  const {
    state: { migraineAttacks, vyduraSchedule },
    dispatch,
  } = useAppContext();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const dateInParams = params.get('day');
  const locationInParams = params.get('location');
  const date = useMemo(
    () => (dateInParams ? new Date(dateInParams) : new Date()),
    [dateInParams]
  );
  const intakeDate: MigraineAttack = useMemo(
    () =>
      migraineAttacks.find((record) =>
        isSameDay(record.date!, date)
      ) as MigraineAttack,
    [migraineAttacks, date]
  );
  const pastAttackDates: Date[] = useMemo(
    () =>
      migraineAttacks
        .filter((record) => !isSameDay(record.date!, date))
        .map((record) => record.date!),
    [date, migraineAttacks]
  );
  const migraineDays: Date[] = useMemo(
    () =>
      migraineAttacks
        .filter((record) => record.isMigraineDay)
        .map((record) => record.date!),
    [migraineAttacks]
  );
  const vyduraEveryOtherDayDates: Date[] = useMemo(
    () =>
      migraineAttacks
        .filter((record) => record.vyduraEveryOtherDay === true)
        .map((record) => record.date!),
    [migraineAttacks]
  );
  const vyduraPerAttackDays: Date[] = useMemo(
    () =>
      migraineAttacks
        .filter((record) => record.vyduraPerAttack === true)
        .map((record) => record.date!),
    [migraineAttacks]
  );
  const [vyduraIntake, setVyduraIntake] = useState(intakeDate);
  const [actionPerformed, setActionPerformed] = useState({
    tomorrow: false,
    delete: false,
  });
  const handleWindowClose = () => {
    if (locationInParams) {
      const params = new URLSearchParams();
      params.set(
        'day',
        vyduraIntake.date
          ? vyduraIntake.date.toDateString()
          : date.toDateString()
      );
      const pathMap = {
        calendar: `${AppRoutes.Calendar}?${params.toString()}`,
        home: AppRoutes.Home,
      };
      navigate(pathMap[locationInParams as keyof typeof pathMap]);
    } else {
      navigate(-1);
    }
  };
  const onSelectIntakeDate = (day: Date) => {
    setVyduraIntake((prev) => ({
      ...prev,
      date: day,
    }));
    setActionPerformed({
      tomorrow: false,
      delete: false,
    });
  };
  const onMoveToTomorrow = () => {
    setVyduraIntake((prev) => ({
      ...prev,
      date: actionPerformed.tomorrow ? date : getNextDate(date),
    }));
    setActionPerformed((prev) => ({
      tomorrow: !prev.tomorrow,
      delete: false,
    }));
  };
  const onDeleteFromToday = () => {
    setVyduraIntake((prev) => ({
      ...prev,
      date: actionPerformed.delete ? date : undefined,
    }));
    setActionPerformed((prev) => ({
      tomorrow: false,
      delete: !prev.delete,
    }));
  };
  const onSaveIntakeDate = () => {
    const lastVyduraDate =
      vyduraSchedule?.endDate ?? new Date(new Date().getFullYear(), 11, 31);
    const futureIntakeDays = getDateRange(
      new Date(vyduraIntake.date!),
      new Date(lastVyduraDate)
    );
    const futureMigraineRecords = getDateRange(
      new Date(vyduraIntake.date!),
      new Date(lastVyduraDate),
      false
    )
      .map((day) => {
        const migraineMatch = migraineAttacks.find(
          (record) => record.isMigraineDay && isSameDay(record.date!, day)
        );
        const isVyduraIntakeDay = isDateInList(day, futureIntakeDays);
        if (migraineMatch) {
          const migraineMatchVyduraType = isVyduraIntakeDay
            ? 'vyduraEveryOtherDay'
            : migraineMatch?.vyduraEveryOtherDay ||
                migraineMatch?.vyduraPerAttack
              ? 'vyduraPerAttack'
              : null;
          return {
            date: day,
            isMigraineDay: true,
            vyduraEveryOtherDay:
              migraineMatchVyduraType === 'vyduraEveryOtherDay',
            vyduraPerAttack: migraineMatchVyduraType === 'vyduraPerAttack',
          };
        }

        return {
          isMigraineDay: false,
          vyduraEveryOtherDay: isVyduraIntakeDay,
          vyduraPerAttack: false,
          date: day,
        };
      })
      .filter((record) => record.isMigraineDay || record.vyduraEveryOtherDay);
    const deleteIntakePayload = migraineAttacks.filter(
      (rec) => !isSameDay(rec.date!, intakeDate.date!)
    );
    const moveAnotherDayPayload = [
      ...migraineAttacks.filter((record) => isBefore(record.date!, date)),
      ...futureMigraineRecords,
    ] as MigraineAttack[];

    dispatch({
      type: ActionType.SET_MIGRAINE_ATTACK,
      payload: {
        migraineAttacks: actionPerformed.delete
          ? deleteIntakePayload
          : moveAnotherDayPayload,
      },
    });
    handleWindowClose();
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <Button
          type='transparent'
          width='content-width'
          style={{ padding: 0, height: '21.6px' }}
          onClick={handleWindowClose}
        >
          <IoMdClose size={20} />
        </Button>
      </div>
      <div className={styles.content}>
        <div className={styles.date}>
          <Text tag='span' size='m-small' color='gray'>
            {vyduraIntake.date
              ? new Intl.DateTimeFormat('en-US', {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                }).format(vyduraIntake.date)
              : ''}
          </Text>
          <DateTimePicker
            showTimePicker={false}
            selectedDate={date}
            handleSelectDateTime={onSelectIntakeDate}
            showAs='button'
            btnText='Edit date'
            // datesDisabled={pastAttackDates}
            vyduraEveryDayDates={vyduraEveryOtherDayDates}
            vyduraPerAttacksDates={vyduraPerAttackDays}
            migraineDates={migraineDays}
          />
        </div>
        <Text tag='h2' size='m-large' weight='bold'>
          Edit Vydura intake
        </Text>
        <Text color='gray' style={{ opacity: 0.8 }}>
          Missed your dose? Choose an option below to update your Vydura intake.
        </Text>
        <Button
          type={actionPerformed.tomorrow ? 'dark-grey' : 'light-grey'}
          width='full-width'
          style={{ width: '100%', marginLeft: 0, marginTop: '32px' }}
          onClick={onMoveToTomorrow}
        >
          Move to {isSameDay(new Date(), date) ? 'tomorrow' : 'next day'}
        </Button>
        <Button
          type={actionPerformed.delete ? 'dark-grey' : 'light-grey'}
          width='full-width'
          style={{ width: '100%', marginLeft: 0, marginTop: '16px' }}
          onClick={onDeleteFromToday}
        >
          Delete from{' '}
          {isSameDay(new Date(), date)
            ? 'today'
            : new Intl.DateTimeFormat('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
              }).format(date)}
        </Button>
      </div>
      <div className={styles.footer}>
        <Button
          type='primary'
          width='full-width'
          onClick={onSaveIntakeDate}
          style={{ width: '100%', marginLeft: 0 }}
          isReadOnly={vyduraIntake.date && isSameDay(vyduraIntake.date, date)}
        >
          Save Record
        </Button>
      </div>
    </div>
  );
};

export default VyduraIntakePage;
